import { z } from 'zod';
import {
  dataHoraSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  tipoDepositoSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Depósitos — o local onde o material está.
 *
 * **Acréscimo à regra de negócio original**: `.spec/010` descreve o saldo de
 * estoque por insumo × empresa e não menciona local nenhum. O depósito entra
 * como a dimensão que faltava: sem ele, "temos 300 sacos de cimento" é verdade
 * na soma e inútil na obra, porque ninguém sabe onde eles estão. A decisão e o
 * que ela implica estão em `.spec/015`.
 *
 * O depósito é **da empresa**, não do tenant: quem movimenta estoque movimenta
 * o estoque de uma empresa do grupo, e o material de uma não entra no saldo da
 * outra.
 */

export const NOME_DEPOSITO_PADRAO = 'Estoque Geral';

/**
 * Tipo do depósito.
 *
 * Classificação e nada além disso — nenhuma regra do sistema lê esta coluna.
 *
 * `OBRA` aqui continua sendo **etiqueta livre**: o usuário classifica como
 * quiser, e nada é deduzido a partir dela. Quem liga o depósito a uma obra de
 * verdade é `obraId`, coluna própria, criada quando a obra nasce. A distinção
 * importa: um depósito marcado `OBRA` à mão não vira depósito de canteiro, e um
 * depósito de canteiro renomeado para `OUTROS` continua amarrado à obra.
 */
export const TIPOS_DEPOSITO = tipoDepositoSchema.options;
export type TipoDeposito = z.infer<typeof tipoDepositoSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_TIPO_DEPOSITO: Readonly<Record<TipoDeposito, string>> = {
  CENTRO_DISTRIBUICAO: 'Centro de Distribuição',
  OBRA: 'Obra',
  OUTROS: 'Outros',
};

/** Explicação de cada tipo, exibida como dica no formulário. */
export const DICA_TIPO_DEPOSITO: Readonly<Record<TipoDeposito, string>> = {
  CENTRO_DISTRIBUICAO: 'Almoxarifado central que abastece as obras.',
  OBRA: 'Estoque mantido no canteiro. O nome é o que identifica a obra.',
  OUTROS: 'Qualquer outro local: contêiner, depósito de terceiro, pátio.',
};

const nomeSchema = z.string().trim().min(2, 'Informe o nome do depósito.').max(200);

/**
 * O payload **não tem `padrao` nem `obraId`**.
 *
 * Quem marca o depósito padrão é o sistema, uma vez, quando a empresa nasce; e
 * quem amarra o depósito à obra é o sistema, uma vez, quando a obra nasce.
 * Aceitar qualquer um dos dois aqui deixaria um `curl` criar a segunda empresa
 * sem padrão nenhum, ou pendurar um depósito qualquer numa obra que já tem o
 * seu — e os índices únicos parciais recusariam a gravação com uma violação de
 * constraint que ninguém consegue interpretar.
 *
 * Nome, descrição e tipo do depósito de canteiro continuam editáveis por aqui.
 * O que ele não aceita é mudar de empresa e ser inativado.
 */
export const criarDepositoSchema = z.object({
  /** Empresa do grupo dona do depósito — `pessoas` com `is_empresa`. */
  empresaId: uuidSchema,
  nome: nomeSchema,
  /** Ausência de descrição é `null`; o formulário converte o `""` do campo. */
  descricao: z.string().trim().max(500).nullable().prefault(null),
  tipo: tipoDepositoSchema,
  ativo: z.boolean().prefault(true),
});

export type CriarDeposito = z.infer<typeof criarDepositoSchema>;
export const atualizarDepositoSchema = criarDepositoSchema;
export type AtualizarDeposito = z.infer<typeof atualizarDepositoSchema>;

export const depositoSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  /** Desnormalizado na leitura, para a listagem não pedir a empresa à parte. */
  empresaNome: z.string().nullable(),
  nome: z.string(),
  descricao: z.string().nullable(),
  tipo: tipoDepositoSchema,
  /**
   * Depósito padrão da empresa, **somente leitura**.
   *
   * Um por empresa, criado junto com ela. A tela usa o campo para esconder a
   * caixa de situação: é o destino garantido de toda movimentação, e inativá-lo
   * deixaria a empresa sem para onde dar entrada.
   */
  padrao: z.boolean(),
  /**
   * Obra do canteiro, **somente leitura**.
   *
   * Um depósito por obra, criado junto com ela. A tela usa o campo pelo mesmo
   * motivo que usa `padrao`: para esconder a caixa de situação. Inativá-lo
   * deixaria a obra sem para onde receber material.
   */
  obraId: uuidSchema.nullable(),
  /** Desnormalizado na leitura, para a listagem não pedir a obra à parte. */
  obraNome: z.string().nullable(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

/**
 * O depósito é protegido contra inativação e exclusão?
 *
 * Duas origens, mesma consequência: o Estoque Geral é o destino garantido da
 * empresa, e o depósito de canteiro é o destino garantido da obra. A função
 * existe para que API e tela não escrevam a condição duas vezes — e a segunda
 * cópia seria a que esqueceria de um dos dois casos.
 */
export function depositoProtegido(deposito: Pick<Deposito, 'padrao' | 'obraId'>): boolean {
  return deposito.padrao || deposito.obraId !== null;
}

export type Deposito = z.infer<typeof depositoSchema>;

export const listaDepositosSchema = respostaListaSchema(depositoSchema);
export type ListaDepositos = z.infer<typeof listaDepositosSchema>;

export const listarDepositosQuerySchema = listagemDeCadastroSchema.extend({
  /** Filtro por empresa: a tela de uma empresa não deve listar o depósito de outra. */
  empresaId: uuidSchema.optional(),
  tipo: tipoDepositoSchema.optional(),
});

export type ListarDepositosQuery = z.infer<typeof listarDepositosQuerySchema>;

export const COLUNAS_ORDENAVEIS_DEPOSITO = ['nome', 'tipo', 'createdAt'] as const;
